import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfilePreview = ({ profile, onRegenerate, onEdit, isGenerating }) => {
  if (!profile) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-12 border border-[#E5E7EB] flex flex-col items-center justify-center min-h-[400px] md:min-h-[500px]">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#F6F7FB] border-2 border-[#E5E7EB] flex items-center justify-center mb-4 md:mb-6">
          <Icon name="FileText" size={32} color="#6B7280" />
        </div>
        <h3 className="text-lg md:text-xl font-semibold text-[#111827] mb-2">No Profile Generated Yet</h3>
        <p className="text-sm md:text-base text-[#6B7280] text-center max-w-md">
          Click "Generate Anonymous Profile" to create your AI-powered professional summary
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#10B981] to-[#059669]">
            <Icon name="CheckCircle" size={20} color="#FFFFFF" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-[#111827]">Generated Profile</h2>
            <p className="text-sm md:text-base text-[#6B7280]">Your anonymous professional summary</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-4 md:p-6 lg:p-8 border border-[#E5E7EB]">
        <div className="prose prose-sm md:prose-base max-w-none">
          <p className="text-sm md:text-base lg:text-lg text-[#111827] leading-relaxed whitespace-pre-line">
            {profile?.content}
          </p>
        </div>

        <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-[#E5E7EB]">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <Button
              variant="outline"
              size="default"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={onRegenerate}
              disabled={isGenerating}
              className="flex-1"
            >
              Regenerate
            </Button>
            <Button
              variant="outline"
              size="default"
              iconName="Edit"
              iconPosition="left"
              onClick={onEdit}
              className="flex-1"
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </div>
      <ProfileStrengthIndicator profile={profile} />
    </div>
  );
};

const ProfileStrengthIndicator = ({ profile }) => {
  const metrics = [
    { label: 'Anonymity', value: profile?.anonymityScore || 95, color: '#10B981' },
    { label: 'Completeness', value: profile?.completenessScore || 88, color: '#5B2DFF' },
    { label: 'Market Appeal', value: profile?.appealScore || 92, color: '#06B6D4' }
  ];

  return (
    <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E5E7EB]">
      <h3 className="text-base md:text-lg font-semibold text-[#111827] mb-4 md:mb-6">Profile Strength</h3>
      <div className="space-y-4 md:space-y-6">
        {metrics?.map((metric, index) => (
          <div key={index}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm md:text-base font-medium text-[#111827]">{metric?.label}</span>
              <span className="text-sm md:text-base font-semibold" style={{ color: metric?.color }}>
                {metric?.value}%
              </span>
            </div>
            <div className="w-full h-2 md:h-3 bg-[#F6F7FB] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${metric?.value}%`,
                  backgroundColor: metric?.color
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfilePreview;